/**
 * P0 QC Workflow - Validation Hook
 * 
 * Custom hook for calling backend validation API.
 * All completion decisions are backend-enforced.
 */

import { useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

/**
 * Hook for order completion validation
 * 
 * @returns {Object} Validation state and functions
 */
export const useCompletionValidation = () => {
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);

  /**
   * Validate order for completion via backend API
   * @param {string} orderId - Order ID to validate
   * @returns {Promise<Object>} Validation result from backend
   */
  const validateForCompletion = useCallback(async (orderId) => {
    setIsValidating(true);
    setValidationError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/orders/${orderId}/validate-completion`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        setValidationResult(result);
        return result;
      } else {
        const errorData = await response.json().catch(() => ({}));
        const error = errorData.detail || 'Validation failed';
        setValidationError(error);
        return { can_complete: false, errors: [{ code: 'API_ERROR', message: error }] };
      }
    } catch (error) {
      console.error('[useCompletionValidation] Error:', error);
      const errorMsg = 'Unable to validate order. Please try again.';
      setValidationError(errorMsg);
      return { can_complete: false, errors: [{ code: 'NETWORK_ERROR', message: errorMsg }] };
    } finally {
      setIsValidating(false);
    }
  }, []);

  /**
   * Clear validation state
   */
  const clearValidation = useCallback(() => {
    setValidationResult(null);
    setValidationError(null);
  }, []);

  return {
    validationResult,
    isValidating,
    validationError,
    validateForCompletion,
    clearValidation,
  };
};

/**
 * Check if QC workflow feature is enabled
 * This is determined by whether the validation endpoint returns qc_enabled info
 */
export const useQCWorkflowStatus = () => {
  const [qcEnabled, setQcEnabled] = useState(null);
  const [isChecking, setIsChecking] = useState(false);

  const checkQCStatus = useCallback(async () => {
    setIsChecking(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/qc/config`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setQcEnabled(data.qc_enabled === true);
        return data.qc_enabled === true;
      }
      // If endpoint fails, assume QC is disabled
      setQcEnabled(false);
      return false;
    } catch (error) {
      console.error('[useQCWorkflowStatus] Error:', error);
      setQcEnabled(false);
      return false;
    } finally {
      setIsChecking(false);
    }
  }, []);

  return {
    qcEnabled,
    isChecking,
    checkQCStatus,
  };
};

/**
 * Format validation errors for display
 * @param {Array} errors - Array of validation error objects
 * @returns {Array} Formatted error messages
 */
export const formatValidationErrors = (errors) => {
  if (!errors || !Array.isArray(errors)) return [];
  
  return errors.map(error => {
    // Add field context if available
    if (error.field) {
      return `${error.message} (${error.field})`;
    }
    return error.message;
  });
};

/**
 * Check if validation result allows completion
 * @param {Object} result - Validation result from backend
 * @returns {boolean} True if order can be completed
 */
export const canCompleteOrder = (result) => {
  if (!result) return false;
  return result.can_complete === true;
};

/**
 * Check if validation result has override requirement
 * @param {Object} result - Validation result from backend
 * @returns {Object|null} Override requirement or null
 */
export const getOverrideRequirement = (result) => {
  if (!result) return null;
  return result.requires_override || null;
};

export default useCompletionValidation;
